/* eslint-disable jira/filename-case */
import parse from 'url-parse';
import { fg } from '@atlassian/jira-feature-gating';
import { sanitiseAssetName } from '@jira/platform__utils-sanitise-asset-name/src/index.js';
import { restUrlsToTrack } from './restUrlsToTrack.tsx';

const isWRMResource = (url: string) => url.includes('/batch.js') || url.includes('/batch.css');
const getWRMAssetName = (url: string) => {
	const [, resources] = /\/([\w.\-,:]*?)(?:,-|\/batch\.)/.exec(url) || [];
	const assetName = sanitiseAssetName(process.env.NODE_ENV !== 'production')(url);

	if (!resources) {
		return assetName;
	}

	return `wrm/${resources}/${assetName}`;
};
const getFileName = (url: string): string =>
	isWRMResource(url)
		? getWRMAssetName(url)
		: sanitiseAssetName(process.env.NODE_ENV !== 'production')(url);

const sanitiseEndpoints = (url: string): string => {
	const parsedUrl = parse(url, true);

	/**
	 * For endpoints with operation or operationName queryParam we will shorten the url
	 * as ufo pipeline will only get the `operation` query param for aggregation
	 */

	const { operation, operationName } = parsedUrl.query;
	let operationNameOverride = operation ?? operationName;

	// Normalize pathname with same replacements to match against restUrlsToTrack
	const normalizedPath = parsedUrl.pathname
		.replace(/\/(project|agent)(-config|s)?\/([A-Z0-9]+)/g, '/$1$2/<project-key>')
		.replace(/\/(servicedesk)(-data)?\/([A-Z0-9]+)/g, '/$1$2/<project-key>')
		.replace(
			/\/(browse|automation|pagination|permissions|async|dialog|itsm)\/([A-Z0-9]+)/g,
			'/$1/<project-key>',
		)
		.replace(/\/(board)(s)?\/([0-9]+)/g, '/$1$2/<board-id>')
		.replace(/\/(\w+)-(\d+)\//i, '/<issue-key>/')
		.replace(/\/pq\/[a-fA-F0-9]+/i, '/pq/<id>') // persisted queries
		.replace(/\/user\/[a-fA-F0-9\-:]+/i, '/user/<user-id>') // engage targeting
		.replace(
			/[a-zA-Z0-9]{8}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{4}-[a-zA-Z0-9]{12}/gi,
			'<uuid>',
		)
		.replace(/\/\d+$/i, '/<id>');

	// Inject synthetic operation names for tracked endpoints when none provided.
	if (!operationNameOverride) {
		for (const { api, operationName: opName } of restUrlsToTrack) {
			if (normalizedPath.includes(api)) {
				operationNameOverride = opName;
				break;
			}
		}
	}

	if (operationNameOverride) {
		return `/${operationNameOverride}?operation=${operationNameOverride}`;
	}

	return normalizedPath;
};

export const jiraResourceTimingsConfig: {
	sanitiseEndpoints: (url: string) => string;
	mapResources: (url: string) => string;
	hasTimingHeaders: (url: string) => boolean;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	xhrFilter: (url: any) => any;
} = {
	sanitiseEndpoints,
	mapResources: getFileName,
	hasTimingHeaders: (url: string): boolean =>
		url.includes('jira-frontend-branch') ||
		url.includes('jira-frontend-bifrost') ||
		(fg('jfp_update_cdn_allowlist') ? url.includes('jira-fe') : false) ||
		isWRMResource(url),
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	xhrFilter: (url: any): any => url.includes('/rest/greenhopper/'),
};
